/**
 * NoteLinkedRecordsSection — slideout section showing every CRM record
 * this note is linked to, grouped by association type.
 *
 * Mounted inside NoteRecordOverview (the per-entity-type overview override
 * registered on `ahoy_note.components.overview`).
 *
 * Read flow:
 *   - Reads outbound associations off the note's CrmObject.
 *   - Filters form-managed types (owner, visibility, creator, mentions,
 *     stage) — same exclusion set NoteCreateModal uses, so the create
 *     flow and the slideout agree on what counts as a "linked record".
 *   - Groups remaining associations by type and renders each as an
 *     `EntityBadgeByIds` row with the schema's friendly label.
 *
 * Write flow:
 *   - "+ Add link" reveals a `LinkPickerRow` (same primitive the create
 *     modal uses) — same association-type Select + entity picker UX.
 *     Commit emits an `ahoy_association_added` event.
 *   - Per-badge × button removes the link (`ahoy_association_removed`).
 *   - Both are gated by `hasPermission('ahoy_note', 'ahoy_association_added' /
 *     '_removed')`; affordances hide entirely when the user lacks the
 *     permission rather than rendering disabled buttons.
 *
 * Empty state: if the note has no linked records AND the user can't add
 * any (no permission), the section renders nothing.
 */

import { useQueryClient } from '@tanstack/react-query';
import { LinkBroken01, Trash01 } from '@untitledui/icons';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks';
import { usePermissions } from '@/hooks/usePermissions';
import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { useCrmObjectSnapshots } from '@/lib/crm/hooks';
import { useAssociationMatchers } from '@/lib/crm/hooks/useAssociationMatchers';
import { useOpenSlideout } from '@/lib/crm/hooks/useOpenSlideout';
import {
  getFailureVersion,
  hasFailedFetch,
  subscribeToFetchFailures,
} from '@/lib/crm/object-cards/entityFetchBatcher';
import { ObjectCard } from '@/lib/crm/object-cards/ObjectCard';
import { crmQueryKeys } from '@/lib/crm/query/queries';
import { submitEventsOptimistic } from '@/lib/crm/store/submitEventsOptimistic';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import { getTypeLabelByName } from '@/lib/crm/utils/getTypeLabel';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';
import { cx } from '@/utils/cx';

import { CollapsibleSection } from '../sections/collapsible-section';
import {
  filterLinkAssociationTypes,
  HIDDEN_ASSOCIATION_TYPES_BY_ENTITY_TYPE,
  toLinkPickerItems,
} from './linkPickerFilters';
import type { LinkedAssociation } from './LinkPickerRow';
import { LinkRecordsEditor } from './LinkRecordsEditor';
import { isMemoEntityType, type MemoEntityType } from './notes.utils';
import { useLinkRecordRows } from './useLinkRecordRows';

interface NoteLinkedRecordsSectionProps {
  /** The ahoy_note or ahoy_task CrmObject backing the slideout. */
  object: CrmObject;
}

export function NoteLinkedRecordsSection({
  object,
}: NoteLinkedRecordsSectionProps) {
  // Every association name, permission check and submitted event below is
  // keyed off the record's own entity type: a task's system edges are
  // `ahoy_task_to_*`, its party links are the case-kind rules a task inherits
  // (`ahoy_deal_to_contact`, source kind `case`), and writing `ahoy_note_to_*`
  // edges onto a task would be rejected. Any other type is a wiring error —
  // this section is registered only on the two note-like overviews.
  const entityType: MemoEntityType = isMemoEntityType(object.entity_type_name)
    ? object.entity_type_name
    : 'ahoy_note';
  const hiddenAssociationTypes =
    HIDDEN_ASSOCIATION_TYPES_BY_ENTITY_TYPE[entityType];

  const { user } = useAuthInfo();
  const { hasPermission } = usePermissions();
  const canAdd = hasPermission(entityType, 'ahoy_association_added');
  const canRemove = hasPermission(entityType, 'ahoy_association_removed');
  const queryClient = useQueryClient();

  const orgId = object.org_id;
  const { associationTypes, matchesSource } = useAssociationMatchers(orgId);

  // Allowed-link set derived from the SAME filter the add picker uses
  // (filterLinkAssociationTypes drops ahoy_import / ahoy_note_pipeline /
  // ahoy_file / ahoy_team targets + `*_timeline` identifiers). The
  // overview's display loop reads from this set so the read side and the
  // write side stay in lockstep — anything you can't link in the form is
  // also hidden from the overview, no matter how it got attached to the
  // note (legacy data, auto-stamping, etc.). HIDDEN_ASSOCIATION_TYPES is
  // applied alongside to drop form-managed types (owner / stage /
  // creator / visibility / mentions) that have dedicated UI elsewhere.
  const allowedAssocTypeNames = useMemo(() => {
    const allowed = new Set<string>();
    for (const at of filterLinkAssociationTypes(associationTypes)) {
      if (hiddenAssociationTypes.has(at.association_type_name)) {
        continue;
      }
      allowed.add(at.association_type_name);
    }
    return allowed;
  }, [associationTypes, hiddenAssociationTypes]);

  // Group target entity_ids by association_type_name so each association
  // type renders as its own labelled row. Anything not in the
  // allowed-link set above is dropped (same filter as the picker).
  const grouped = useMemo(() => {
    const groups = new Map<string, string[]>();
    for (const assoc of object.associations ?? []) {
      if (!allowedAssocTypeNames.has(assoc.association_type_name)) {
        continue;
      }
      const targetId = assoc.target_entity_id;
      if (!targetId) {
        continue;
      }
      const existing = groups.get(assoc.association_type_name);
      if (existing) {
        existing.push(targetId);
      } else {
        groups.set(assoc.association_type_name, [targetId]);
      }
    }
    return groups;
  }, [object.associations, allowedAssocTypeNames]);

  // Flat list of all linked entity IDs (with their association type for removal).
  const linkedEntities = useMemo(() => {
    const result: { entityId: string; associationTypeName: string }[] = [];
    for (const [assocTypeName, entityIds] of grouped.entries()) {
      for (const entityId of entityIds) {
        result.push({ entityId, associationTypeName: assocTypeName });
      }
    }
    return result;
  }, [grouped]);

  // Linkable outbound association types for the add picker. Built by
  // narrowing `associationTypes` to rows outbound from THIS record's type —
  // kind-aware, so a task (a case) is offered the case-kind rules it
  // inherits, not only the type-named `ahoy_task_to_*` ones — then
  // intersecting with `allowedAssocTypeNames` so the picker and the display
  // loop share ONE filter config — adding/removing an entry in
  // EXCLUDED_LINK_TARGETS or the hidden set updates both surfaces at once.
  const linkableAssocTypes = useMemo(
    () =>
      associationTypes.filter(
        at =>
          matchesSource(at, entityType) &&
          allowedAssocTypeNames.has(at.association_type_name)
      ),
    [associationTypes, matchesSource, allowedAssocTypeNames, entityType]
  );

  const linkPickerItems = useMemo(
    () => toLinkPickerItems(linkableAssocTypes),
    [linkableAssocTypes]
  );

  // Immediate-commit picker state. Rows live in the shared `useLinkRecordRows`
  // hook (below); a completed row is persisted right away (handleRowComplete)
  // and then dropped — no separate Save step, matching the composer's UX.
  const [isSubmitting, setIsSubmitting] = useState(false);

  // `removeRow` comes from the hook below; capture it in a ref so the
  // completion handler — which is passed INTO the hook — can drop the
  // committed row without a definition cycle.
  const removeRowRef = useRef<(id: string) => void>(() => {});

  /** Refresh both the note's list query AND the slideout's focused-object
   *  query after a mutation so the rendered associations match the new
   *  server state AND `object.last_event_seq` advances ahead of the next
   *  edit. Without the focused-object invalidation the seq stays stale
   *  and a follow-up update/delete races into a sequence conflict.
   *
   *  Returns a Promise that resolves once both refetches complete — callers
   *  MUST await it before allowing another mutation, otherwise a fast
   *  second click submits against the stale `last_event_seq` and the
   *  backend rejects it with a sequence conflict.
   */
  const invalidate = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: crmQueryKeys.objects(orgId, entityType),
        }),
        queryClient.invalidateQueries({
          queryKey: ['crm', orgId, 'object', object.entity_id],
        }),
      ]),
    [queryClient, orgId, object.entity_id, entityType]
  );

  // Persist a link the instant its row is complete (association type + ≥1
  // target), then drop the row — the new association renders in the linked-
  // records list above. Mirrors the composer's inline "Link record" UX with no
  // separate Save step.
  const handleRowComplete = useCallback(
    async (row: LinkedAssociation) => {
      const { associationType } = row;
      if (!user?.id || !associationType || row.targetEntityIds.length === 0) {
        return;
      }
      setIsSubmitting(true);
      try {
        await submitEventsOptimistic({
          orgId,
          userId: user.id,
          entityTypeName: entityType,
          primaryEntityId: object.entity_id,
          fallbackSeq: object.last_event_seq,
          build: builder => {
            for (const targetEntityId of row.targetEntityIds) {
              builder.associationAdded(
                object.entity_id,
                entityType,
                associationType.association_type_name,
                targetEntityId
              );
            }
          },
        });
        // Await invalidation so `object.last_event_seq` advances before any
        // follow-up mutation — see invalidate() docstring.
        await invalidate();
        removeRowRef.current(row.id);
      } catch (err) {
        logger.error(
          'NoteLinkedRecordsSection: failed to add link',
          err instanceof Error ? err : new Error(String(err)),
          { org_id: orgId, entity_id: object.entity_id }
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      user?.id,
      object.entity_id,
      object.last_event_seq,
      orgId,
      entityType,
      invalidate,
    ]
  );

  const {
    rows: pickerRows,
    addRow: handleAddRow,
    removeRow: handleRemoveRow,
    setAssociationType: handleAssociationTypeChange,
    setTarget: handleTargetChange,
  } = useLinkRecordRows(linkableAssocTypes, {
    onRowComplete: handleRowComplete,
  });
  removeRowRef.current = handleRemoveRow;

  const handleBadgeRemove = useCallback(
    async (associationTypeName: string, targetEntityId: string) => {
      if (!user?.id) {
        return;
      }
      setIsSubmitting(true);
      try {
        await submitEventsOptimistic({
          orgId,
          userId: user.id,
          entityTypeName: entityType,
          primaryEntityId: object.entity_id,
          fallbackSeq: object.last_event_seq,
          build: builder => {
            builder.associationRemoved(
              object.entity_id,
              entityType,
              associationTypeName,
              targetEntityId
            );
          },
        });
        // Await invalidation so `object.last_event_seq` advances before any
        // follow-up mutation — see invalidate() docstring.
        await invalidate();
      } catch (err) {
        logger.error(
          'NoteLinkedRecordsSection: failed to remove link',
          err instanceof Error ? err : new Error(String(err)),
          { org_id: orgId, entity_id: object.entity_id }
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      user?.id,
      object.entity_id,
      object.last_event_seq,
      orgId,
      entityType,
      invalidate,
    ]
  );

  // Show the "Link record" affordance whenever the user has permission, even on
  // notes with no existing links — otherwise a user with add permission can
  // never link the first record from this surface.
  const showAddPanel = canAdd;

  // Nothing to show AND no permission to add → render nothing. Avoid an
  // empty accordion header.
  if (grouped.size === 0 && !canAdd) {
    return null;
  }

  return (
    <CollapsibleSection title='Linked records' defaultExpanded>
      <div className='flex flex-col gap-1.5 pt-1.5'>
        {linkedEntities.map(({ entityId, associationTypeName }) => (
          <LinkedEntityCard
            key={`${associationTypeName}:${entityId}`}
            entityId={entityId}
            associationTypeName={associationTypeName}
            orgId={orgId}
            canRemove={canRemove}
            isSubmitting={isSubmitting}
            onRemove={handleBadgeRemove}
          />
        ))}

        {showAddPanel && (
          // A touch more breathing room above the "+ Link record" affordance so
          // it reads as a separate action from the list of linked rows above it,
          // beyond the row-to-row gap.
          <div className='mt-0.5'>
            <LinkRecordsEditor
              orgId={orgId}
              rows={pickerRows}
              items={linkPickerItems}
              linkableCount={linkableAssocTypes.length}
              onAddRow={handleAddRow}
              onRemoveRow={handleRemoveRow}
              onAssociationTypeChange={handleAssociationTypeChange}
              onTargetChange={handleTargetChange}
              isSubmitting={isSubmitting}
              addButtonColor='link-color'
            />
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

interface LinkedEntityCardProps {
  entityId: string;
  associationTypeName: string;
  orgId: number;
  canRemove: boolean;
  isSubmitting: boolean;
  onRemove: (associationTypeName: string, targetEntityId: string) => void;
}

/**
 * Renders a linked entity as a flat bordered row with an entity icon,
 * display name, and entity type label. Clicking navigates to the entity
 * slideout. A trash icon appears on hover when removal is permitted.
 */
function LinkedEntityCard({
  entityId,
  associationTypeName,
  orgId,
  canRemove,
  isSubmitting,
  onRemove,
}: LinkedEntityCardProps) {
  const openSlideout = useOpenSlideout();
  const snapshots = useCrmObjectSnapshots([entityId], orgId);
  const obj = snapshots.get(entityId);

  // Subscribe to fetch-failure changes so the card re-renders when a fetch
  // fails, transitioning from `loading` → `failed` instead of spinning
  // forever on stale/broken links. Without this, a deleted target entity
  // (or a 4xx/5xx fetch) leaves the card stuck on a skeleton.
  useSyncExternalStore(subscribeToFetchFailures, getFailureVersion);
  const fetchFailed = !obj && hasFailedFetch(entityId);

  // Use the canonical getDisplayName helper rather than a hand-rolled
  // properties.ahoy_display_name?.value read: list-query snapshots store
  // properties as raw values (not PropertyValue wrappers), so the direct
  // access returned undefined and rows flashed "Untitled" until a richer
  // fetch landed. getDisplayName handles both formats and composes
  // first/last name for optimistic contacts.
  const displayName = obj ? getDisplayName(obj, 'Untitled') : '';

  const handleClick = useCallback(() => {
    openSlideout(entityId);
  }, [openSlideout, entityId]);

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove(associationTypeName, entityId);
    },
    [associationTypeName, entityId, onRemove]
  );

  // Parent ObjectCard handles Enter/Space to open the slideout. Stop keydown
  // bubble on the trash button so keyboard remove doesn't also navigate.
  const handleRemoveKeyDown = useCallback((e: React.KeyboardEvent) => {
    e.stopPropagation();
  }, []);

  const typeLabel = obj ? getTypeLabelByName(obj.entity_type_name) : '';
  const Icon = obj ? getEntityTypeIconComponent(obj.entity_type_name) : null;

  // Subtle rounded tile that houses the entity-type icon — a flat "app icon"
  // treatment that makes the row's type scannable at a glance without adding
  // colour noise (the right-aligned type label carries the label).
  const tileClass =
    'flex size-6 shrink-0 items-center justify-center rounded-md bg-[var(--color-bg-tertiary)]';

  // Remove affordance shared by both states. In the normal row it stacks in
  // the same grid cell as the type label (below) so revealing it on hover
  // never shifts the row; here (fetch-failed, no type label) it simply fades
  // in on hover.
  const removeButton = canRemove && !isSubmitting && (
    <button
      type='button'
      onClick={handleRemove}
      onKeyDown={handleRemoveKeyDown}
      className='p-1 rounded-md text-fg-quaternary opacity-0 transition-all group-hover:opacity-100 focus-visible:opacity-100 hover:text-utility-error-600 hover:bg-utility-error-50'
      aria-label='Remove link'
    >
      <Trash01 className='size-3.5' />
    </button>
  );

  if (fetchFailed) {
    return (
      <ObjectCard bordered compact>
        <div className='group flex items-center gap-2'>
          <span className={tileClass}>
            <LinkBroken01 className='size-4 text-fg-quaternary' />
          </span>
          <div className='min-w-0 flex-1'>
            <p className='text-sm text-fg-tertiary truncate'>
              Linked record unavailable
            </p>
          </div>
          <span className='shrink-0'>{removeButton}</span>
        </div>
      </ObjectCard>
    );
  }

  return (
    <ObjectCard bordered compact onClick={handleClick}>
      <div className='group flex items-center gap-2'>
        <span className={tileClass}>
          {Icon ? (
            <Icon className='size-4 text-fg-quaternary' />
          ) : (
            <span className='size-4' />
          )}
        </span>
        <div className='min-w-0 flex-1'>
          {obj ? (
            <p className='text-sm font-medium text-fg-primary truncate'>
              {displayName}
            </p>
          ) : (
            <Skeleton variant='text' width={100} height={14} />
          )}
        </div>
        {/* Type label + remove button share ONE grid cell (sized to the wider
            of the two) so the trash fading in on hover never widens the row
            or nudges the label. On hover the label fades out as the trash
            fades in. `items-center` (not `place-items-end`) vertically centers
            them with the entity name — the taller hidden trash button would
            otherwise bottom-align the label and push it below the name. */}
        <span className='grid shrink-0 items-center justify-items-end'>
          {typeLabel && (
            <span
              className={cx(
                // `pointer-events-none` is load-bearing: this label and the
                // trash button share ONE grid cell, and the label's
                // group-hover:opacity-0 gives it opacity < 1, which creates a
                // stacking context that paints the (now-invisible) label ON TOP
                // of the trash — so on hover the trash appeared but the label
                // swallowed the click ("icon shows on hover but can't be
                // clicked"). The label is purely decorative, so dropping its
                // pointer events lets the click fall through to the trash.
                'pointer-events-none col-start-1 row-start-1 text-xs text-fg-tertiary transition-opacity',
                // Only fade the label out on hover when there's a trash button
                // to reveal in its place — otherwise a no-permission row's
                // label would vanish on hover with nothing behind it.
                canRemove && !isSubmitting && 'group-hover:opacity-0'
              )}
            >
              {typeLabel}
            </span>
          )}
          {canRemove && !isSubmitting && (
            <span className='col-start-1 row-start-1'>{removeButton}</span>
          )}
        </span>
      </div>
    </ObjectCard>
  );
}
